import React, { ReactElement, useContext, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import enumStrings, { RoutePaths } from "../../../enumStrings";
import PasswordField from "../../common/PasswordField/PasswordField";
import { fetchAuthSession, signIn, signOut } from "aws-amplify/auth";
import {
  GetUserDocument,
  GetUserQuery,
  ItemsPurchasableByChamberEnum,
  UserType,
} from "../../../graphql/types";
import BaseAuthPage from "./BaseAuthPage";
import Button from "../../common/Button";
import LinkText from "../../common/LinkText";
import TextField from "../../common/TextField";
import { ApolloContextValue, getApolloContext } from "@apollo/client";

interface LoginInput {
  email: string;
  password: string;
}

export const signInHandler = async (
  email: string,
  password: string,
  apolloClient: ApolloContextValue
): Promise<void> => {
  try {
    const user = await signIn({
      username: email,
      password,
    });

    const authSession = await fetchAuthSession();

    if (
      authSession &&
      authSession.tokens &&
      authSession.tokens.accessToken &&
      authSession.tokens.accessToken.payload &&
      authSession.tokens.accessToken.payload["custom:isDisabled"] === "true"
    ) {
      await signOut();

      // eslint-disable-next-line
      throw { name: "UserDisabled" };
    }

    if (
      authSession &&
      authSession.tokens &&
      authSession.tokens.accessToken &&
      authSession.tokens.accessToken.payload &&
      // @ts-ignore
      authSession.tokens.accessToken.payload["cognito:groups"][0]
    ) {
      if (
        // @ts-ignore
        authSession.tokens.accessToken.payload["cognito:groups"][0] !==
        UserType.ChamberAdmin
        //   &&
        // // @ts-ignore
        // authSession.tokens.accessToken.payload["cognito:groups"][0] !==
        //   UserType.ChamberMemberAdmin
      ) {
        // eslint-disable-next-line
        throw { name: "UserDisabled" };
      }
    }

    if (
      user.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    ) {
      sessionStorage.setItem(
        enumStrings.sessionStorageKey.loggedInWithTemporaryPassword,
        JSON.stringify({
          email: email.trim().toLowerCase(),
          tempPassword: password,
        })
      );
    } else {
      // ensure user has access to chamber member pro
      if (
        authSession &&
        authSession.tokens &&
        authSession.tokens.accessToken &&
        authSession.userSub
      ) {
        const customAccessTokenToCheckIfLoggingInUserHasAccessToChamberMemberPro =
          authSession.tokens.accessToken.toString();

        const userData = (await apolloClient.client?.query({
          query: GetUserDocument,
          variables: { userId: authSession.userSub },
          fetchPolicy: "network-only",
          context: {
            customAccessTokenToCheckIfLoggingInUserHasAccessToChamberMemberPro,
          },
        })) as { data: GetUserQuery | undefined };

        if (
          !(
            userData?.data?.getUser?.chamberInfo?.purchasedItems || []
          ).includes(ItemsPurchasableByChamberEnum.ChamberMemberPro)
        ) {
          await signOut();

          // eslint-disable-next-line
          throw { name: "UserDoesNotHaveAccessToChamberMemberPro" };
        }
      }
    }

    window.location.href = RoutePaths.home;
  } catch (error: any) {
    throw error;
  }
};

const validationSchema = yup.object({
  email: yup.string().required(enumStrings.pages.common.validation.email),
  password: yup.string().required(enumStrings.pages.common.validation.password),
});

const Login = (): ReactElement => {
  const apolloClient = useContext(getApolloContext());

  const [customError, setCustomError] = useState("");
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const initialValues: LoginInput = {
    email: "",
    password: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: LoginInput) => {
      setCustomError("");
      setLoader(true);
      try {
        await signInHandler(
          input.email.trim().toLowerCase(),
          input.password,
          apolloClient
        );
        setButtonDisabled(true);
      } catch (error: any) {
        if (error?.name === "NetworkError") {
          setCustomError(enumStrings.pages.common.errors.networkError);
        } else if (error?.name === "NotAuthorizedException") {
          setCustomError(
            enumStrings.pages.common.errors.invalidUsernameOrPassword
          );
        } else if (error?.name === "UserNotFoundException") {
          setCustomError(
            enumStrings.pages.common.errors.invalidUsernameOrPassword
          );
        } else if (error?.name === "UserDisabled") {
          setCustomError(
            enumStrings.pages.common.errors.invalidUsernameOrPassword
          );
        } else if (error?.name === "UserDoesNotHaveAccessToChamberMemberPro") {
          setCustomError(
            enumStrings.pages.common.errors
              .userDoesNotHaveAccessToChamberMemberPro
          );
        }
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  return (
    <BaseAuthPage logoSubtitle={enumStrings.pages.login.logoSubtitle}>
      <form
        style={{
          width: "100%",
        }}
        onSubmit={formik.handleSubmit}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <TextField
            id="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            fullWidth
            helperText={formik.touched.email && formik.errors.email}
          />
          <PasswordField
            id="password"
            fieldName="password"
            label="Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.errors.password}
            touched={formik.touched.password}
            disablePopup
          />

          <Button
            text={enumStrings.pages.login.buttonText}
            disabled={buttonDisabled || loader}
            isLoading={loader}
            fullWidth
            size="large"
            type="submit"
            errorText={customError}
            additionalSx={{
              mt: 4,
            }}
          />
          <LinkText
            to={RoutePaths.forgotPassword}
            text={enumStrings.pages.login.forgotPasswordText}
          />
        </Box>
      </form>
    </BaseAuthPage>
  );
};

export default Login;
